import { useMemo, FormEvent } from 'react'
import { xor, union, without } from 'lodash'

import { Accordion, Checkbox, Flex, Typography } from '@percent/lemonade'
import { TagsListProps } from './TagsList.types'
import style from './TagsList.module.scss'
import {
  usePostHogFeatureFlag,
  POSTHOG_FEATURE_FLAGS
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'

export function TagsList({ activityTags, onSubTagChange, onTagChange, subTags, tags }: TagsListProps) {
  const addNewProgramFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_ADD_NEW_PROGRAM)

  const activityTagsToDisplay = useMemo(
    () =>
      activityTags
        ?.map(tag => ({
          ...tag,
          subTags: tag.subTags.filter(subTag => !subTag.deprecatedAt)
        }))
        .filter(tag => tag.subTags.length > 0) ?? [],
    [activityTags]
  )

  const handleTagChange = (e: FormEvent<HTMLInputElement>, tagId: string) => {
    const isChecked = e.currentTarget.checked
    const tag = activityTagsToDisplay.find(t => t.id === tagId)

    if (!tag) {
      return
    }

    const tagSubTagIds = tag.subTags.map(subTag => subTag.id)

    onTagChange(prev => xor(prev, [tagId]))

    if (isChecked) {
      onSubTagChange(prev => union(prev, tagSubTagIds))
    } else {
      onSubTagChange(prev => without(prev, ...tagSubTagIds))
    }
  }

  const handleSubTagChange = (e: FormEvent<HTMLInputElement>, subTagId: string) => {
    const isChecked = e.currentTarget.checked

    onSubTagChange(prev => xor(prev, [subTagId]))

    const parentTag = activityTagsToDisplay.find(tag => tag.subTags.some(subTag => subTag.id === subTagId))

    if (parentTag) {
      const parentTagSubTagIds = parentTag.subTags.map(subTag => subTag.id)
      const updatedSubTagsForm = isChecked ? union(subTags, [subTagId]) : without(subTags, subTagId)

      const someSubTagsChecked = parentTagSubTagIds.some(id => updatedSubTagsForm.includes(id))

      if (someSubTagsChecked) {
        onTagChange(prev => union(prev, [parentTag.id]))
      } else {
        onTagChange(prev => without(prev, parentTag.id))
      }
    }
  }

  return (
    <Accordion
      classNames={{
        root: style.accordionRoot,
        control: style.accordionControl,
        item: style.accordionItem
      }}
      multiple
    >
      {activityTagsToDisplay?.map(tag => (
        <Accordion.Item key={tag.id} value={tag.name}>
          <Flex className={style.tag} align="center">
            <Checkbox
              dataTestId={tag.id}
              active={!!tags?.includes(tag.id)}
              name={tag.name}
              label={<></>}
              disabled={!addNewProgramFeatureFlag}
              variant={
                !!tags?.includes(tag.id) && !tag.subTags.map(subTag => subTag.id).every(id => subTags?.includes(id))
                  ? 'indeterminate'
                  : 'default'
              }
              onChange={e => handleTagChange(e, tag.id)}
            />
            <Accordion.Control>
              <Flex align="center" gap={4}>
                <Typography variant="default">{tag.name}</Typography>
                <Typography variant="default" $color="gray100">
                  ({tag.subTags.length})
                </Typography>
              </Flex>
            </Accordion.Control>
          </Flex>
          <Accordion.Panel>
            <Flex direction="column" gap={8}>
              {tag.subTags.map(subTag => (
                <div key={subTag.id} className={style.subTagWrapper}>
                  <Checkbox
                    dataTestId={subTag.id}
                    active={!!subTags?.includes(subTag.id)}
                    name={subTag.name}
                    label={subTag.name}
                    variant="default"
                    onChange={e => handleSubTagChange(e, subTag.id)}
                    disabled={!addNewProgramFeatureFlag}
                  />
                </div>
              ))}
            </Flex>
          </Accordion.Panel>
        </Accordion.Item>
      ))}
    </Accordion>
  )
}
